"use client";

import { BadgeCheck, BadgeDollarSign, CreditCard, Handshake, MapPin, Repeat, Smartphone, TrendingUp, Wallet, Wrench, Zap } from "lucide-react";
import MarketingNav from "@/components/marketing/MarketingNav";
import MarketingFooter from "@/components/marketing/MarketingFooter";
import PartnerLeadForm from "@/components/marketing/PartnerLeadForm";
import { SeminarSection } from "@/components/marketing/SeminarSection";
import {
  Band,
  BenefitCards,
  CtaButton,
  FaqGrid,
  FloatingCard,
  FormFootnote,
  FormSection,
  FounderSection,
  HeroCard,
  LandingHero,
  NoticeBox,
  Section,
  SectionHead,
  StepsGrid,
  YesNo,
  useForcedLang,
} from "@/components/marketing/landing/LandingBlocks";
import { useLang } from "@/contexts/language-context";

/**
 * /agentes: reclutamiento de agentes de ventas desde la marca personal de
 * Samuel. Reglas del plan de reclutamiento (docs/marketing/plan-reclutamiento-
 * agentes.md): cero cifras para el agente, nunca "residual" ni jerga, no es
 * MLM (se cobra por negocios, no por reclutar), trabajo real. Los montos se
 * explican en la llamada.
 *
 * La línea (Samuel, 2026-10-09): lo más importante de una venta es tener un
 * producto ganador, y Redy lo es. Aquí no se vende nada: se le mejora el
 * sistema al dueño, con inversión casi nula y sin pagar nada al mes. El
 * mensaje es "tengo un producto ganador y me falta gente que lo ofrezca".
 * Nunca se juzga el oficio del vendedor ni se le dice que empieza de cero.
 */
export default function AgentesClient({
  forcedLang,
  founderPhoto,
  seminarUrl,
}: {
  forcedLang: "es" | "en" | null;
  founderPhoto: string | null;
  /** Calendario GHL del seminario de vendedores; sin él la página queda como siempre. */
  seminarUrl?: string | null;
}) {
  const { t } = useLang();
  useForcedLang(forcedLang);
  const cta = t("Call me", "Quiero que me llamen");
  // Con seminario, ese es el llamado principal y el formulario pasa a ser la alternativa.
  const hasSeminar = Boolean(seminarUrl?.trim());

  return (
    <>
      <MarketingNav />
      <main>
        <LandingHero
          eyebrow={t("Redy agents · DFW", "Agentes de Redy · DFW")}
          title={t("I have a winning product.", "Tengo un producto ganador.")}
          accent={t("I need people to offer it.", "Me falta gente que lo ofrezca.")}
          lead={t(
            "The hardest part of selling is having something worth selling. That part is solved: a system restaurant owners want once they understand it, because it improves their business and the software plan is free when they process payments with Redy. What's missing is people who can explain it.",
            "Lo más difícil de vender es tener algo que valga la pena vender. Eso ya está resuelto: un sistema que el dueño de restaurante quiere cuando lo entiende, porque mejora su negocio y el plan de software es gratis si procesa sus pagos con Redy. Lo que falta es gente que se lo explique."
          )}
          cta={cta}
          secondary={{ label: t("See the product", "Ver el producto"), href: "#producto" }}
          trust={[
            { icon: Handshake, label: t("Nothing is sold. It's explained.", "No se vende: se explica") },
            { icon: Wallet, label: t("No money down", "Sin poner dinero") },
            { icon: MapPin, label: t("Dallas – Fort Worth", "Dallas – Fort Worth") },
          ]}
          visual={<AgentesVisual />}
        />

        <SeminarSection audience="agents" calendarUrl={seminarUrl} />

        <Band
          items={[
            { value: "$0", label: t("Start plan for the owner", "plan Start para el dueño"), sub: t("with Redy payments, month to month", "con pagos de Redy, mes a mes") },
            { value: "Dual", label: t("pricing built in", "precio dual incluido"), sub: t("card payers cover the processing cost", "quien paga con tarjeta cubre el procesamiento") },
            { value: "0", label: t("experience needed", "experiencia necesaria"), sub: t("we teach you how to explain it", "te enseñamos a explicarlo") },
            { value: "1×", label: t("you explain it", "lo explicas"), sub: t("and get paid every month while they stay open", "y cobras cada mes mientras siga abierto") },
          ]}
        />

        {/* El producto */}
        <Section id="producto">
          <SectionHead
            eyebrow={t("The product", "El producto")}
            title={t("Why it's a winning product.", "Por qué es un producto ganador.")}
            muted={t("Nothing is sold. It's explained.", "No se vende. Se explica.")}
            lead={t(
              "The owner's system gets better and their costs go down. When they understand that, the product does the rest.",
              "Al dueño le mejora el sistema y le bajan los costos. Cuando lo entiende, el producto hace el resto."
            )}
          />
          <BenefitCards
            items={[
              {
                icon: CreditCard,
                title: t("Card fees, covered", "Comisiones de tarjeta, cubiertas"),
                body: t("With dual pricing, the card price is designed to cover the processing cost, so it doesn't come out of the owner's margin.", "Con precio dual, el precio con tarjeta está pensado para cubrir el costo de procesamiento, así no sale del margen del dueño."),
              },
              {
                icon: BadgeDollarSign,
                title: t("$0 software plan", "Plan de software $0"),
                body: t("Free software plan when they process payments with Redy. Month to month, no long-term contract.", "Plan de software gratis si procesan sus pagos con Redy. Mes a mes, sin contrato a largo plazo."),
              },
              {
                icon: Wrench,
                title: t("Install and support included", "Instalación y soporte incluidos"),
                body: t("Redy installs, trains and stays. You don't carry that.", "Redy instala, enseña y acompaña. Eso no lo cargas tú."),
              },
              {
                icon: BadgeCheck,
                title: t("Tested in the founder's kitchen", "Probado en la cocina del fundador"),
                body: t("It runs every day at the founder's own restaurant, Empanada Factory in Dallas, before it reaches anyone else.", "Corre todos los días en el restaurante del propio fundador, Empanada Factory en Dallas, antes de llegar a nadie más."),
              },
            ]}
          />
        </Section>

        <Section id="como" tone="soft">
          <SectionHead
            eyebrow={t("How it works", "Cómo funciona")}
            title={t("We give you the product and the system.", "Te damos el producto y el sistema.")}
            muted={t("You explain it.", "Tú lo explicas.")}
          />
          <StepsGrid
            items={[
              {
                title: t("We teach you how to explain it", "Te enseñamos a explicarlo"),
                body: t(
                  "An academy from zero: what the product is, why it works for the owner, how it gets installed. Nobody starts knowing.",
                  "Academia desde cero: qué es el producto, por qué le conviene al dueño, cómo se instala. Nadie empieza sabiendo."
                ),
              },
              {
                title: t("You talk to owners", "Hablas con dueños"),
                body: t(
                  "You get the platform to manage your contacts and build proposals, and the system that brings you interested businesses.",
                  "Te damos la plataforma para manejar tus contactos y armar propuestas, y el sistema que te trae negocios interesados."
                ),
              },
              {
                title: t("You get paid every month", "Cobras cada mes"),
                body: t(
                  "Every business you activate pays you every month while it stays open. That's what makes it different from a paycheck.",
                  "Cada negocio que activas te paga todos los meses mientras siga abierto. Eso es lo que lo hace distinto de un sueldo."
                ),
              },
            ]}
          />
        </Section>

        <Section>
          <SectionHead
            eyebrow={t("What you get", "Qué ganas")}
            title={t("A base that is yours", "Una base que es tuya")}
            muted={t("and grows.", "y crece.")}
          />
          <BenefitCards
            items={[
              { icon: Repeat, title: t("Income that repeats", "Ingreso que se repite"), body: t("Not a one-time commission. Every month, every business.", "No una comisión de una vez. Cada mes, cada negocio.") },
              { icon: TrendingUp, title: t("A base that grows", "Una base que crece"), body: t("Each business you activate adds to the ones before it.", "Cada negocio que activas se suma a los anteriores.") },
              { icon: Wallet, title: t("No money down", "Sin poner dinero"), body: t("No inventory, no office hours, no fees to join.", "Sin inventario, sin horario de oficina, sin cuotas para entrar.") },
              { icon: Zap, title: t("A product that explains itself", "Un producto que se explica solo"), body: t("The software plan is free with Redy payments, and card payers cover the processing cost. Once they get it, they want it.", "El plan de software es gratis con pagos de Redy, y quien paga con tarjeta cubre el costo de procesamiento. Cuando lo entiende, lo quiere.") },
            ]}
          />
          <NoticeBox title={t("About the numbers:", "Sobre los números:")}>
            {t(
              "The exact amounts are explained on the call, with the agreement in hand. We don't put them on a page because they depend on how many hours you have and what you activate.",
              "Los montos exactos se explican en la llamada, con el acuerdo en la mano. No los ponemos en una página porque dependen de cuántas horas tienes y de qué activas."
            )}
          </NoticeBox>
        </Section>

        <Section tone="soft">
          <SectionHead
            eyebrow={t("Straight talk", "Sin letra chica")}
            title={t("What this is.", "Qué es esto.")}
            muted={t("And what it isn't.", "Y qué no es.")}
          />
          <YesNo
            yesTitle={t("What it is", "Qué sí es")}
            yes={[
              t("A winning product that gets explained, not pushed.", "Un producto ganador que se explica, no se empuja."),
              t("Real work: you go out and talk to owners.", "Trabajo real: sales a hablar con dueños."),
              t("Independent: your hours, your zone, our system.", "Independiente: tus horas, tu zona, nuestro sistema."),
              t("Paid for businesses that activate and stay open.", "Pagado por negocios que activan y siguen abiertos."),
            ]}
            noTitle={t("What it is NOT", "Qué NO es")}
            no={[
              t("A pyramid: you earn for businesses, not for recruiting.", "Pirámide: cobras por negocios, no por reclutar."),
              t("Easy money: nobody gets paid for sitting at home.", "Dinero fácil: nadie cobra por quedarse en casa."),
              t("A job: there's no fixed salary and no boss.", "Un empleo: no hay sueldo fijo ni jefe."),
              t("A purchase: you don't buy a kit, a license or a course.", "Una compra: no compras kit, licencia ni curso."),
            ]}
          />
        </Section>

        <Section>
          <SectionHead
            eyebrow={t("Who this is for", "Para quién es")}
            title={t("Who says yes.", "Quién dice que sí.")}
            muted={t("And who shouldn't.", "Y quién no debería.")}
          />
          <YesNo
            yesTitle={t("Yes, if you…", "Sí, si tú…")}
            yes={[
              t("Have sold something before: insurance, cars, phones, whatever. You already have the hard part.", "Has vendido algo alguna vez: seguros, carros, celulares, lo que sea. Ya tienes lo difícil."),
              t("Are bilingual and live in DFW or nearby.", "Eres bilingüe y vives en DFW o cerca."),
              t("Like the street more than a desk.", "Te gusta más la calle que el escritorio."),
              t("Want to build something that is yours.", "Quieres construir algo tuyo."),
            ]}
            noTitle={t("No, if you…", "No, si tú…")}
            no={[
              t("Need a fixed salary from day one.", "Necesitas sueldo fijo desde el día uno."),
              t("Don't want to talk to people.", "No quieres hablar con gente."),
              t("Expect results without going out.", "Esperas resultados sin salir."),
            ]}
          />
        </Section>

        <FounderSection
          photo={founderPhoto}
          eyebrow={t("Who's behind the product", "Quién está detrás del producto")}
          quote={t(
            "I built the product and tested it in my own restaurant. Now I need people to take it to the rest.",
            "Construí el producto y lo probé en mi propio restaurante. Ahora necesito gente que lo lleve a los demás."
          )}
          extraLine={t(
            "I started carrying plates. Eight years at Marriott, from busboy to F&B manager. Founder of Redy and owner of Empanada Factory in Dallas, where the system gets tested first.",
            "Empecé cargando platos. Ocho años en Marriott, de cargar platos a gerente de alimentos y bebidas. Fundador de Redy y dueño de Empanada Factory en Dallas, donde el sistema se prueba primero."
          )}
        />

        <Section tone="soft">
          <SectionHead eyebrow="FAQ" title={t("Questions we", "Preguntas que")} muted={t("get a lot", "nos hacen seguido")} center />
          <FaqGrid
            items={[
              {
                q: t("Do I have to sell?", "¿Tengo que vender?"),
                a: t(
                  "No. You have to explain it. When the owner understands the software plan is free with Redy payments and card payers cover the processing cost, the product does the rest.",
                  "No. Tienes que explicarlo. Cuando el dueño entiende que el plan de software es gratis con pagos de Redy y que quien paga con tarjeta cubre el costo de procesamiento, el producto hace el resto."
                ),
              },
              { q: t("Do I need sales experience?", "¿Necesito experiencia en ventas?"), a: t("It helps, but it's not required. If you like talking to people, the rest is taught.", "Ayuda, pero no es obligatoria. Si te gusta hablar con gente, lo demás se enseña.") },
              { q: t("Is it full time?", "¿Es tiempo completo?"), a: t("You decide. We ask how many hours you have, and the plan fits that.", "Lo decides tú. Preguntamos cuántas horas tienes y el plan se arma con eso.") },
              { q: t("What happens after the call?", "¿Qué pasa después de la llamada?"), a: t("If it fits, you register on the platform and start the first module of the academy.", "Si hay encaje, te registras en la plataforma y empiezas el primer módulo de la academia.") },
            ]}
          />
          <div className="mt-14 flex justify-center">
            <CtaButton label={cta} />
          </div>
        </Section>

        <FormSection
          eyebrow={t("Next step", "Siguiente paso")}
          title={hasSeminar ? t("I'd rather you contact me", "Prefiero que me contacten") : cta}
          lead={t(
            "Leave your number and I call you. Fifteen minutes: I show you the product and how it's offered.",
            "Deja tu número y te llamo. Quince minutos: te muestro el producto y cómo se ofrece."
          )}
          points={[
            t("What you've sold before.", "Qué has vendido antes."),
            t("How many hours you have.", "Cuántas horas tienes."),
            t("The product, how it's offered, and the numbers with the agreement in hand.", "El producto, cómo se ofrece y los números con el acuerdo en la mano."),
          ]}
          footnote={
            <FormFootnote>
              {t(
                "By submitting you agree to a call and to text messages about this opportunity. Terms:",
                "Al enviar aceptas que te llamemos y te escribamos por mensaje de texto sobre esta oportunidad. Términos de"
              )}
            </FormFootnote>
          }
        >
          <PartnerLeadForm door="agente" />
        </FormSection>
      </main>
      <MarketingFooter />
    </>
  );
}

/** Visual del hero: lo que le ofreces al dueño (el producto) y, debajo, la
 *  forma del ingreso del agente (se acumula mes a mes). Sin cifras de dinero
 *  para el agente, por regla del plan. */
function AgentesVisual() {
  const { t } = useLang();
  const product = [
    t("$0 software plan with Redy payments", "Plan de software $0 con pagos de Redy"),
    t("Dual pricing: card payers cover the fee", "Precio dual: la tarjeta cubre la comisión"),
    t("Install and support included", "Instalación y soporte incluidos"),
    t("Month to month", "Mes a mes"),
  ];
  const months = [1, 1, 2, 2, 3, 4, 4, 5];

  return (
    <div className="relative">
      <HeroCard kicker={t("What you offer the owner", "Lo que le ofreces al dueño")}>
        <ul className="mt-5 space-y-2.5">
          {product.map((line) => (
            <li key={line} className="flex items-center gap-2.5 rounded-[14px] bg-white/[0.04] border border-white/[0.06] px-3.5 py-2.5">
              <span className="w-5 h-5 rounded-full grid place-items-center shrink-0" style={{ background: "linear-gradient(135deg,#D7263D,#F4722B)" }} aria-hidden>
                <BadgeCheck className="w-3 h-3 text-white" />
              </span>
              <span className="text-[13px] font-bold text-white">{line}</span>
            </li>
          ))}
        </ul>

        <div className="mt-6 pt-5 border-t border-white/10">
          <div className="flex items-end justify-between mb-3">
            <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-white/40">{t("What you earn · example", "Lo que cobras · ejemplo")}</p>
            <p className="text-[11px] text-white/45 font-medium">{t("every month", "cada mes")}</p>
          </div>
          <div className="flex items-end gap-1.5 h-16" aria-hidden>
            {months.map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-t-[4px]"
                style={{
                  height: `${(h / 5) * 100}%`,
                  background: i === months.length - 1 ? "linear-gradient(180deg,#F4722B,#D7263D)" : "rgba(255,255,255,0.14)",
                }}
              />
            ))}
          </div>
          <p className="text-[12px] text-white/55 mt-3 leading-snug">
            {t("Every business you activate stacks on the last one.", "Cada negocio que activas se suma al anterior.")}
          </p>
        </div>
      </HeroCard>

      <FloatingCard from="right" delay={0.9} className="-right-[170px] -top-7">
        <div className="w-10 h-10 bg-redy-green/10 rounded-full flex items-center justify-center shrink-0">
          <BadgeCheck className="w-[18px] h-[18px] text-redy-green" />
        </div>
        <div>
          <div className="text-[12px] font-black text-redy-charcoal leading-tight">{t("Owner activated", "Dueño activado")}</div>
          <div className="text-[10px] font-bold text-redy-secondary">{t("Installed by Redy. Yours from now on.", "Lo instala Redy. Tuyo desde hoy.")}</div>
        </div>
      </FloatingCard>

      <FloatingCard from="left" delay={1.1} className="-left-[190px] bottom-[18%]">
        <div className="w-10 h-10 bg-redy-orange/10 rounded-full flex items-center justify-center shrink-0">
          <Smartphone className="w-[18px] h-[18px] text-redy-orange" />
        </div>
        <div>
          <div className="text-[12px] font-black text-redy-charcoal leading-tight">{t("Academy · Module 1", "Academia · Módulo 1")}</div>
          <div className="text-[10px] font-bold text-redy-secondary">{t("Done from your phone.", "Se hace desde el celular.")}</div>
        </div>
      </FloatingCard>
    </div>
  );
}
